import { ChangeLogPopUp, LoadingPopup, TripUpdatePopup, UpdatePopUp } from 'components/pop-up';
import { AppSettingContext, LoggingContext, NavigationEntryContext, TripContext } from 'contexts';
import React, { Fragment, useContext, useEffect, useState } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { useLoadManagerService, useNavigation, useSettingService, useRequestCache } from 'services';
import { TripChange } from 'services/types';
import { isOnline } from 'utils';
import { messageSW, Workbox } from 'workbox-window';
import appInfo from './app-info';
import routes from './app-routes';
import { SideNavOuterToolbar as SideNavBarLayout } from './layouts';
import { envConfig } from 'config';

export default function () {

    const [readyForSync, setReadyForSync] = useState<boolean>(false)
    const [changes, setChanges] = useState<TripChange[]>([])
    const [isViewingChanges, setIsViewingChanges] = useState<boolean>(false)
    const [isUpdateAvailable, setIsUpdateAvailable] = useState<boolean>(false)
    const [swRegistration, setSwRegistration] = useState<ServiceWorkerRegistration>({} as ServiceWorkerRegistration)
    const [isChangelogPopupVisible, setIsChangelogPopupVisible] = useState<boolean>(false)

    const location = useLocation()
    const tripContext = useContext(TripContext)
    const settingsContext = useContext(AppSettingContext)
    const navContext = useContext(NavigationEntryContext)
    const dbLogContext = useContext(LoggingContext)
    
    const config = envConfig();
    const showUpdatePopUp: boolean = `${config.SHOW_UPDATE_POPUP}` === 'true'

    const isAppReady = tripContext.isReady() && settingsContext.isReady() && navContext.isReady()

    const { customTitle, navigateBack, navigateTo } = useNavigation()
    const { syncRequests, getLoadAssignments } = useLoadManagerService()
    const { setAppUpdated, getAppUpdated, getAppVersion, setAppVersion } = useSettingService();

    const currentRoute = routes.find(route => route.path == location.pathname)
    const title: string | null = customTitle()
    const routeTitle = currentRoute ? currentRoute.title : appInfo.title
    const lastAppVersion = getAppVersion();
    const currentAppVersion: string = process.env.REACT_APP_VERSION ?? ''

    useEffect(() => {
        if ('serviceWorker' in navigator) {
            if (showUpdatePopUp) {
                const wb = new Workbox('service-worker.js')

            wb.addEventListener('installed', event => {
                if (event.isUpdate) {
                    setIsUpdateAvailable(true)
                }
            });

                wb.register().then(r => {
                    setIsUpdateAvailable(r?.waiting != null)
                    setSwRegistration(r ?? {} as ServiceWorkerRegistration)
                })
            } else {
                navigator.serviceWorker.getRegistrations().then(regs => {
                    regs.forEach(r => r.unregister())
                })
                if (window.caches) {
                    caches.keys().then(names => names.forEach(n => caches.delete(n)))
                }
            }
        }

        //handle browser/device back button to behave the same way as the in-app back button
        window.addEventListener('popstate', function (e) {
            this.window.history.pushState({}, '')
            // Don't do a normal back, because the browser has already moved the actual page.
            navigateBack(undefined, true)
            // Grab the original relative URL, with parameters.
            let url = window.location.href;
            let idx = url.lastIndexOf('/');
            if (idx > 0) {
                url = url.substring(idx)
            }
            // Force navigate to the relative URL so the navigation stack gets updated.  This is required
            // because the user could be going "back" to a deeper view in the navigation, not just a
            // shallower view.
            navigateTo(url)
        })

    }, [])

    useEffect(() => {
        const isAppUpdated: boolean = getAppUpdated()
        if (isAppReady && (lastAppVersion !== currentAppVersion)) {
            setIsChangelogPopupVisible(isAppUpdated)
        }
    }, [isAppReady])

    const updateWorker = async () => {
        setAppUpdated(true)
        if (swRegistration && swRegistration?.waiting != null) {
            let updated = await messageSW(swRegistration.waiting, { type: 'SKIP_WAITING' })
            if (updated) {
                window.location.reload()
            }
        }
    }

    const handleChangelogClose = () => {
        setAppVersion(currentAppVersion)
        setIsChangelogPopupVisible(false)
        setAppUpdated(false)
    }

    const purgeDbOperationLogs = () => {
        //delete all db logs older than 15 minutes
        const logCutoff = new Date().getTime() - (15 * 60 * 1000)
        const oldLogIds: number[] = dbLogContext.dbOperations.filter(log => log.dateMs < logCutoff).map(log => log.id!)
        dbLogContext.dbOperations.bulkRemove(oldLogIds)
    }

    useEffect(() => {
        const interval = setInterval(async () => {
            if (await isOnline()) {
                setReadyForSync(true)
            }
        }, 15 * 60 * 1000)

        return () => clearInterval(interval)
    }, [])

    useEffect(() => {
        //just in case the session state gets changed before the method ends, grab the value before we alter the session state.
        const sync = async () => {
            try {

                try {
                    const wasSyncSuccessful = await syncRequests(true)
                    if (wasSyncSuccessful && !isViewingChanges && tripContext.trips.toArray() != null) {
                        let changes: TripChange[] = await getLoadAssignments()
                        setChanges(changes)
                        setIsViewingChanges(changes.length > 0)
                    }
                }
                catch (e) {
                    console.error(e)
                }

                try {
                    purgeDbOperationLogs()
                }
                catch (e) {
                    console.error(e)
                }
            }
            finally {
                setReadyForSync(false)
            }
        }

        if (readyForSync) {
            sync()
        }
    }, [readyForSync])

    window.ononline = () => {
        setReadyForSync(true)
    }

    return (
        <Fragment>
            {isAppReady &&
                <SideNavBarLayout backButtonVisible={currentRoute?.backButtonVisible} title={title ?? routeTitle}>
                    <Routes>
                        {routes.map(({ path, component }) => (
                            <Route key={path} path={path} element={React.createElement(component)} />
                        ))}
                        <Route path="*" element={<Navigate to={'load-assignments'} />} />
                    </Routes>
                </SideNavBarLayout>
            }

            <TripUpdatePopup
                isVisible={isViewingChanges}
                changes={changes}
                onClickOk={() => setIsViewingChanges(false)}
            />
            <LoadingPopup visible={!isAppReady} />
            <UpdatePopUp visible={isUpdateAvailable} onUpdate={updateWorker} />
            <ChangeLogPopUp visible={isChangelogPopupVisible} appVersion={currentAppVersion} onClickOk={handleChangelogClose} />
        </Fragment>
    );
}